import { entityEffects, selectCurrentEntity, selectEntityDisplayName } from '@opengov/app-core/entity';
import { getEntity, getEntitySubdomain, getNumericalEntityId } from '@opengov/app-core/entity/selectors';
import { coreFeatureFlagEffects } from '@opengov/app-core/featureFlag';
import { getSession, userSelector } from '@opengov/app-core/session/selectors';
import { ensureAll } from '@opengov/compose-app-router/onEnter';
import { getLocation } from '@opengov/compose-app-router/selectors';
import { routerActions } from '@opengov/compose-app-router/state';
import { STORIES_SUPER_ADMIN_PRIVILEGE } from '@opengov/stories-shared/constants';
import { generatePageTitle } from '@opengov/stories-shared/utils';

import { storiesMsgs } from '../../locale';
import { fetchCurrentSubjectClaims } from '../api/subjectClaimsApi';
import {
  IPublicationCustomCssProps,
  PublicationCustomCss
} from '../components/Publications/PublicationCustomCss';
import { selectOnlinePublicationTranslatorFlag } from '../common/flags';
import { hasPrivilege } from '../utils';

import { onEnterStoriesRoutes } from '../../routes/onEnter';
import { maintenancePath, storyListPath } from '../../routes/paths';
import { navigateToPublicationView } from '../state/navigation';

interface ICustomCssRouteParams {
  entitySubdomain: string;
  publicationId: string;
  storyId: string;
}

// `state: any` is required by `IRoute['selector']` from
// `@opengov/compose-app-router` — narrowing breaks route-level
// assignability against the library's `(state: any) => any` contract.
const publicationCustomCssSelector = (state: any): IPublicationCustomCssProps => {
  const location = getLocation(state);
  const { publicationId, storyId, entitySubdomain: urlEntitySubdomain } =
    (location.params || {}) as ICustomCssRouteParams;
  const storeEntitySubdomain = getEntitySubdomain(state);
  // After a refresh the entity store briefly holds the previous entity
  // until `fetchRequestedOrCurrentEntity` resolves. Return entityId=0
  // while the store subdomain doesn't match the URL so query hooks stay
  // disabled and don't fire against the wrong entity.
  const entityId = urlEntitySubdomain && storeEntitySubdomain === urlEntitySubdomain
    ? getNumericalEntityId(state)
    : 0;
  const msgs = storiesMsgs(state);
  return {
    entityId,
    entitySubdomain: storeEntitySubdomain,
    entityUuid: getEntity(state).uuid || '',
    publicationId: publicationId || '',
    storyId: storyId || '',
    messages: { ...msgs.publicationCustomCss },
    dialogMessages: { ...msgs.publicationCustomCssDialogs },
    user: userSelector(state),
    enableGoogleTranslateForPublications: selectOnlinePublicationTranslatorFlag(state)
  };
};

export const publicationCustomCssRoute = {
  name: 'PublicationCustomCss',
  // `state: any` matches `ITitleFunction` from @opengov/compose-app-router.
  title: (state: any) => generatePageTitle('Publication Custom CSS', selectEntityDisplayName(state)),
  selector: publicationCustomCssSelector,
  component: PublicationCustomCss,
  onEnter: ensureAll([
    onEnterStoriesRoutes,
    async (state, nextLocation, onSuccess, onError) => {
      const { entitySubdomain, publicationId } = nextLocation.params as ICustomCssRouteParams;
      await entityEffects
        .fetchRequestedOrCurrentEntity({
          requestedEntitySubdomain: entitySubdomain,
          currentEntity: selectCurrentEntity(state)
        })
        .then(async (entity) => {
          if (entity.subdomain !== entitySubdomain) {
            return onError(
              'Entity not found...',
              routerActions.replace.create({ pathname: storyListPath(entity.subdomain) })
            );
          }
          const currentSubjectClaims = await fetchCurrentSubjectClaims({
            userUuid: getSession(state).uuid || '',
            entityUuid: entity.uuid || ''
          });
          const isStoriesSuperAdmin = hasPrivilege(currentSubjectClaims, STORIES_SUPER_ADMIN_PRIVILEGE);

          await coreFeatureFlagEffects.getFlags().then(async (flags) => {
            if (flags && !flags['product-stories-feature-custom-css-code'] && !isStoriesSuperAdmin) {
              navigateToPublicationView(entitySubdomain, publicationId);
            }

            if (flags && flags['product-stories-maintenance-window']) {
              return onError(
                'Redirecting to maintenance page...',
                routerActions.replace.create({ pathname: maintenancePath(entitySubdomain) })
              );
            }
            onSuccess();
          });
        });
    }
  ])
};
